/* eslint-disable no-param-reassign */
import React, { useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import { VbzModal, VbzButton } from '@smiles/voebiz-ui-kit';
import { ModalBody } from 'reactstrap';
import { useForm } from 'react-hook-form';
import ControlledDropdown from '../ControlledDropdown';
import { handleKeyPressLettersAndNumbers, formatDate } from '../../utils';
import { defaultValues } from './mock';
import ControlledInput from '../ControlledInput';
import IconCalendar from '../IconCalendar';
import SuccessModal from '../SuccessModal';
import ErrorModal from '../ErrorModal';
import InterruptedModal from '../InterruptedModal';
import LoadingModal from '../LoadingModal';
import { postCreditRequest, getAirportList, handleAirportList } from '../../services/services';

type CreditRequestFormT = {
  icon: JSX.Element;
  isOpenModalForm: boolean;
  toggleFormModal: () => void;
};
const CreditRequestModalForm = ({ icon, isOpenModalForm, toggleFormModal }: CreditRequestFormT) => {
  const {
    register,
    control,
    errors,
    formState: { isValid },
    handleSubmit,
    getValues,
    watch,
  } = useForm({
    mode: 'onChange',
    defaultValues,
  });
  const { t } = useTranslation('creditRequestForm');
  const [openMessageModal, setOpenMessageModal] = useState(false);
  const [airportList, setAirportList] = useState<
    {
      name: string;
      description: string;
    }[]
  >([
    {
      name: '',
      description: '',
    },
  ]);
  const [modalType, setModalType] = useState<`success` | `error` | `interrupted` | `loading`>('interrupted');
  const ldFlags = useFlags();
  const createRequestService = async (newRequestData: any) => {
    const originAirport = newRequestData['origin-airport'].split(', ');
    const regex = /\((.*?)\)/;
    const match = regex.exec(originAirport[2]);
    let originAirportName;
    if (match) {
      originAirportName = match[1];
    }
    const requestApiBody = {
      isG3: '', //
      recordLocator: newRequestData['input-flight-code'],
      flightDate: formatDate(newRequestData['flight-date']),
      partnerAirLine: '', //
      firstNameTicket: '', //
      lastNameTicket: '', //
      electronicTicketNumber: newRequestData['input-ticket-number'],
      electronicTicketUrl: '', //
      flightList: [
        {
          flightDate: formatDate(newRequestData['flight-date']),
          fligthNumber: newRequestData['input-flight-code'],
          departureAirport: {
            name: originAirportName,
            code: '',
            city: originAirport[0],
            country: originAirport[1],
          },
          arrivalAirport: {
            name: '',
            code: '',
            city: '',
            country: '',
          },
          travelClass: '', //
          bookingClass: '', //
          boardingPassUrl: '', //
        },
      ],
    };
    const { apiDomain } = ldFlags;
    const createRequestServiceResponse = await postCreditRequest(requestApiBody, apiDomain);
    if (createRequestServiceResponse.data) {
      if (createRequestServiceResponse.data.status === 'Points in processing') {
        setModalType('success');
      } else {
        setModalType('error');
      }
    } else {
      setModalType('error');
    }
  };
  const normalizeDate = (value: string) => {
    return value
      .replace(/^(\d\d)(\d)$/g, '$1/$2')
      .replace(/^(\d\d\/\d\d)(\d+)$/g, '$1/$2')
      .replace(/[^\d/]/g, '');
  };
  const dateInputRangeError = (inputDate = '') => {
    const [day, month, year] = inputDate.split('/');
    const flightDate: Date = new Date(`${year}-${month}-${day}`);
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 31 * 24 * 60 * 60 * 1000);
    const threeDaysAgo = new Date(today.getTime() - 4 * 24 * 60 * 60 * 1000);
    const isInRange = flightDate >= thirtyDaysAgo && flightDate <= threeDaysAgo;
    return isInRange;
  };
  const onSubmit = (data: any) => {
    setModalType('loading');
    setOpenMessageModal(!openMessageModal);
    toggleFormModal();
    createRequestService(data);
  };
  const getAirportListService = async (description: string) => {
    const { airportDomainApi } = ldFlags;
    const getAirportListServiceResponse = await getAirportList(description, airportDomainApi);
    const organizedList = handleAirportList(getAirportListServiceResponse, description);
    const result: {
      name: string;
      description: string;
    }[] = [];
    organizedList.map((item: any) => {
      result.push({
        name: item.label,
        description: item.value,
      });
      return null;
    });
    setAirportList(result);
    return result;
  };
  useEffect(() => {
    const airportInput = getValues('origin-airport');
    if (airportInput && airportInput.length > 2) {
      getAirportListService(getValues('origin-airport'));
    }
  }, [watch('origin-airport')]);
  return (
    <>
      <VbzModal
        desktopSize="big"
        isOpen={isOpenModalForm}
        toggle={() => {
          setModalType('interrupted');
          setOpenMessageModal(!openMessageModal);
          toggleFormModal();
        }}
      >
        <ModalBody>
          <form
            onSubmit={handleSubmit((data) => {
              onSubmit(data);
            })}
          >
            <div className="credit-request-form-wrapper">
              <div className="credit-request-form-header">
                <div className="credit-request-form-header__icon">{icon}</div>
                <h3 className="credit-request-form-header__title">{t('credit_request_form_title')}</h3>
              </div>
              <div className="credit-request-form-information">
                <div className="credit-request-form-information__background">
                  <p className="credit-request-form-information__text">{t('information_card')}</p>
                </div>
              </div>
              <div className="input-content-wrapper">
                <div className="input-content-wrapper-left">
                  <ControlledDropdown
                    name="company-name"
                    textLabel={t('company')}
                    errorMessage={t('company_error_message')}
                    actions={[
                      {
                        name: 'GOL Linhas Aéreas',
                        description: 'descrição de teste',
                      },
                    ]}
                    rules={{ required: true }}
                    placeholder={t('company_name_gol_placeholder')}
                    control={control}
                    error={Boolean(errors['company-name'])}
                  />
                  <ControlledInput
                    name="input-flight-code"
                    textLabel={t('flight_code')}
                    control={control}
                    placeholder={t('flight_code_placeholder')}
                    errorMessage={t('flight_code_error_message')}
                    error={Boolean(errors['input-flight-code'])}
                    onKeyDown={handleKeyPressLettersAndNumbers}
                    maxLength={6}
                    inputMode="text"
                    rules={{ pattern: /^[a-z0-9]+$/i, required: true, maxLength: 6, minLength: 6 }}
                  />
                  <ControlledDropdown
                    textLabel={t('origin_airport')}
                    errorMessage={t('origin_airport_error_message')}
                    actions={airportList}
                    placeholder={t('origin_airport_placeholder')}
                    control={control}
                    name="origin-airport"
                    rules={{ required: true }}
                    error={Boolean(errors['origin-airport'])}
                  />
                </div>
                <div className="input-content-wrapper-right">
                  <ControlledInput
                    control={control}
                    name="input-ticket-number"
                    inputMode="text"
                    textLabel={t('ticket_number')}
                    placeholder={t('ticket_number_placeholder')}
                    errorMessage={t('ticket_number_error_message')}
                    maxLength={13}
                    rules={{ pattern: /^127.{10}$/, required: true, maxLength: 13, minLength: 13 }}
                    onKeyDown={handleKeyPressLettersAndNumbers}
                    error={Boolean(errors['input-ticket-number'])}
                  />
                  <ControlledInput
                    inputMode="numeric"
                    textLabel={t('flight-date')}
                    placeholder={t('flight-date_placeholder')}
                    rules={{
                      required: true,
                      maxLength: 10,
                      minLength: 10,
                      validate: { dateError: dateInputRangeError },
                    }}
                    errorMessage={t('flight-date_error_message')}
                    control={control}
                    maxLength={10}
                    onKeyDown={handleKeyPressLettersAndNumbers}
                    name="flight-date"
                    error={Boolean(errors['flight-date'])}
                    icon={<IconCalendar />}
                    regex="/[^0-9/]/gi"
                    normalizeFunction={normalizeDate}
                    register={register}
                  />
                </div>
              </div>
            </div>
            <VbzButton
              id="submit"
              className="vbz-request-button"
              color="primary"
              text={t('send_button')}
              type="submit"
              disabled={!isValid}
            />
          </form>
        </ModalBody>
      </VbzModal>
      {modalType === 'success' && (
        <SuccessModal isOpen={openMessageModal} onClosed={() => setOpenMessageModal(false)} />
      )}
      {modalType === 'error' && (
        <ErrorModal
          isOpen={openMessageModal}
          onClosed={() => setOpenMessageModal(false)}
          reOpenFormModal={toggleFormModal}
        />
      )}
      {modalType === 'interrupted' && (
        <InterruptedModal
          isOpen={openMessageModal}
          onClosed={() => setOpenMessageModal(false)}
          reOpenFormModal={toggleFormModal}
        />
      )}
      {modalType === 'loading' && (
        <LoadingModal isOpen={openMessageModal} onClosed={() => setOpenMessageModal(false)} />
      )}
    </>
  );
};
export default CreditRequestModalForm;
